import { useState, useEffect } from 'react';
import { useAuth } from '../App';
import { subscriptionsApi } from '../lib/api';
import { formatCurrency, formatRelativeTime } from '../lib/utils';
import AppLayout from '../components/AppLayout';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { Crown, Check, Users, DollarSign, Calendar, Loader2, Star, X } from 'lucide-react';

const SubscriptionsPage = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('browse');
  const [plans, setPlans] = useState([]);
  const [mySubscriptions, setMySubscriptions] = useState({ subscriptions: [], total_monthly_spend: 0, active_count: 0 });
  const [mySubscribers, setMySubscribers] = useState({ subscribers: [], total_monthly_revenue: 0, plans: [], subscriber_count: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [subscribingTo, setSubscribingTo] = useState(null);
  const [cancellingId, setCancellingId] = useState(null);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [plansRes, mySubsRes, subscribersRes] = await Promise.all([
        subscriptionsApi.getPlans(),
        subscriptionsApi.getMy(),
        subscriptionsApi.getSubscribers(),
      ]);
      setPlans(plansRes.data);
      setMySubscriptions(mySubsRes.data);
      setMySubscribers(subscribersRes.data);
    } catch (error) {
      toast.error('Failed to load subscription data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubscribe = async (planId) => {
    setSubscribingTo(planId);
    try {
      const res = await subscriptionsApi.subscribe(planId);
      toast.success(`Subscribed to ${res.data.plan}!`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to subscribe');
    } finally {
      setSubscribingTo(null);
    }
  };

  const handleCancel = async (subId) => {
    setCancellingId(subId);
    try {
      await subscriptionsApi.cancel(subId);
      toast.success('Subscription cancelled');
      fetchData();
    } catch (error) {
      toast.error('Failed to cancel subscription');
    } finally {
      setCancellingId(null);
    }
  };

  if (isLoading) {
    return (
      <AppLayout>
        <div className="p-6 lg:p-0 space-y-6"><Skeleton className="h-8 w-48" /><Skeleton className="h-48 w-full rounded-xl" /><Skeleton className="h-64 w-full rounded-xl" /></div>
      </AppLayout>
    );
  }

  // Group plans by creator
  const creatorPlans = {};
  plans.forEach(plan => {
    if (!creatorPlans[plan.creator_wallet_id]) {
      creatorPlans[plan.creator_wallet_id] = {
        name: plan.creator_name,
        avatar: plan.creator_avatar,
        plans: []
      };
    }
    creatorPlans[plan.creator_wallet_id].plans.push(plan);
  });

  return (
    <AppLayout>
      <div className="min-h-screen lg:min-h-0 pb-24 lg:pb-0">
        {/* Header */}
        <div className="p-6 lg:p-0 lg:mb-6">
          <h1 className="text-2xl lg:text-3xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <Crown className="w-7 h-7 inline mr-2 text-yellow-500" />Subscriptions
          </h1>
          <p className="text-muted-foreground">Support creators with monthly subscriptions</p>
        </div>

        <div className="lg:grid lg:grid-cols-3 lg:gap-6">
          <div className="lg:col-span-2">
            {/* Stats */}
            <div className="px-6 lg:px-0 mb-6 grid grid-cols-3 gap-3">
              <div className="bg-card border border-white/10 rounded-xl p-4">
                <Crown className="w-5 h-5 text-yellow-500 mb-2" />
                <p className="text-xs text-muted-foreground">My Subscriptions</p>
                <p className="text-xl font-bold">{mySubscriptions.active_count}</p>
              </div>
              <div className="bg-card border border-white/10 rounded-xl p-4">
                <DollarSign className="w-5 h-5 text-muted-foreground mb-2" />
                <p className="text-xs text-muted-foreground">Monthly Spend</p>
                <p className="text-xl font-bold">{formatCurrency(mySubscriptions.total_monthly_spend)}</p>
              </div>
              <div className="bg-card border border-white/10 rounded-xl p-4">
                <Users className="w-5 h-5 text-green-500 mb-2" />
                <p className="text-xs text-muted-foreground">My Subscribers</p>
                <p className="text-xl font-bold text-green-500">{mySubscribers.subscriber_count}</p>
              </div>
            </div>

            <div className="px-6 lg:px-0">
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="grid w-full grid-cols-3 mb-6">
                  <TabsTrigger value="browse">Browse Plans</TabsTrigger>
                  <TabsTrigger value="active">My Subs</TabsTrigger>
                  <TabsTrigger value="subscribers">Subscribers</TabsTrigger>
                </TabsList>

                <TabsContent value="browse" className="space-y-6">
                  {Object.entries(creatorPlans).map(([walletId, creator]) => (
                    <div key={walletId}>
                      <div className="flex items-center gap-3 mb-4">
                        {creator.avatar ? <img src={creator.avatar} alt="" className="w-10 h-10 rounded-full object-cover" /> : <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center font-bold">{creator.name?.charAt(0)}</div>}
                        <h3 className="font-semibold">{creator.name}</h3>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {creator.plans.map((plan) => (
                          <div key={plan.plan_id} className={`bg-card border rounded-xl p-5 ${plan.is_subscribed ? 'border-green-500/30' : 'border-white/10'}`} data-testid={`plan-${plan.plan_id}`}>
                            <div className="flex items-center justify-between mb-3">
                              <h4 className="font-medium">{plan.name}</h4>
                              {plan.is_subscribed && <span className="text-xs bg-green-500/20 text-green-500 px-2 py-0.5 rounded-full">Active</span>}
                            </div>
                            <p className="text-2xl font-bold text-primary mb-4">{formatCurrency(plan.price)}<span className="text-sm text-muted-foreground font-normal">/month</span></p>
                            <ul className="space-y-2 mb-4">
                              {plan.benefits.map((b, idx) => (
                                <li key={idx} className="flex items-start gap-2 text-sm"><Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" /><span className="text-muted-foreground">{b}</span></li>
                              ))}
                            </ul>
                            <div className="flex items-center justify-between text-xs text-muted-foreground mb-4">
                              <span>{plan.subscriber_count} subscribers</span>
                            </div>
                            {plan.is_subscribed ? (
                              <Button variant="outline" className="w-full border-green-500/30 text-green-500" disabled>Subscribed</Button>
                            ) : (
                              <Button onClick={() => handleSubscribe(plan.plan_id)} disabled={subscribingTo === plan.plan_id} className="w-full bg-primary hover:bg-red-600" data-testid={`subscribe-${plan.plan_id}`}>
                                {subscribingTo === plan.plan_id ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}Subscribe
                              </Button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </TabsContent>

                <TabsContent value="active" className="space-y-3">
                  {mySubscriptions.subscriptions.length === 0 ? (
                    <div className="text-center py-12 bg-card border border-white/10 rounded-xl"><Crown className="w-12 h-12 text-muted-foreground mx-auto mb-4" /><h3 className="font-semibold mb-2">No active subscriptions</h3><p className="text-muted-foreground text-sm">Browse plans above to get started!</p></div>
                  ) : mySubscriptions.subscriptions.map((sub) => (
                    <div key={sub.subscription_id} className={`bg-card border rounded-xl p-4 ${sub.status === 'active' ? 'border-white/10' : 'border-white/5 opacity-60'}`}>
                      <div className="flex items-center gap-4">
                        {sub.subscriber_avatar && <img src={sub.subscriber_avatar} alt="" className="w-12 h-12 rounded-full object-cover" />}
                        <div className="flex-1">
                          <p className="font-medium">{sub.plan_name || sub.creator_name}</p>
                          <p className="text-sm text-muted-foreground">{sub.creator_name} &middot; {formatCurrency(sub.amount)}/mo</p>
                          <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1"><Calendar className="w-3 h-3" />Next billing: {new Date(sub.next_billing).toLocaleDateString()}</p>
                        </div>
                        <div className="text-right">
                          <span className={`text-xs px-2 py-1 rounded-full ${sub.status === 'active' ? 'bg-green-500/20 text-green-500' : 'bg-white/10 text-muted-foreground'}`}>{sub.status}</span>
                          {sub.status === 'active' && (
                            <button onClick={() => handleCancel(sub.subscription_id)} disabled={cancellingId === sub.subscription_id} className="block mt-2 text-xs text-red-500 hover:underline" data-testid={`cancel-${sub.subscription_id}`}>
                              {cancellingId === sub.subscription_id ? 'Cancelling...' : 'Cancel'}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </TabsContent>

                <TabsContent value="subscribers" className="space-y-3">
                  {mySubscribers.subscriber_count === 0 ? (
                    <div className="text-center py-12 bg-card border border-white/10 rounded-xl"><Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" /><h3 className="font-semibold mb-2">No subscribers yet</h3><p className="text-muted-foreground text-sm">{user?.user_type === 'creator' ? 'Share your subscription plans to attract supporters!' : 'Become a creator to offer subscription plans'}</p></div>
                  ) : mySubscribers.subscribers.map((sub) => (
                    <div key={sub.subscription_id} className="flex items-center gap-3 p-3 bg-card border border-white/10 rounded-xl">
                      {sub.subscriber_avatar ? <img src={sub.subscriber_avatar} alt="" className="w-10 h-10 rounded-full object-cover" /> : <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center"><Users className="w-5 h-5 text-primary" /></div>}
                      <div className="flex-1"><p className="font-medium text-sm">{sub.subscriber_name}</p><p className="text-xs text-muted-foreground">Since {new Date(sub.started_at).toLocaleDateString()}</p></div>
                      <span className="text-green-500 font-medium text-sm">{formatCurrency(sub.amount)}/mo</span>
                    </div>
                  ))}
                </TabsContent>
              </Tabs>
            </div>
          </div>

          {/* Right Sidebar */}
          <div className="lg:col-span-1 mt-6 lg:mt-0 px-6 lg:px-0 space-y-6">
            {/* Revenue */}
            {user?.user_type === 'creator' && mySubscribers.total_monthly_revenue > 0 && (
              <div className="bg-card border border-white/10 rounded-xl p-5 card-accent relative overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-1 bg-green-500" />
                <p className="text-muted-foreground text-sm mb-1">Monthly Revenue</p>
                <p className="text-3xl font-bold text-green-500">{formatCurrency(mySubscribers.total_monthly_revenue)}</p>
                <p className="text-xs text-muted-foreground mt-1">{mySubscribers.subscriber_count} active subscribers</p>
              </div>
            )}

            {/* My Plans */}
            {mySubscribers.plans.length > 0 && (
              <div className="bg-card border border-white/10 rounded-xl p-5">
                <h3 className="font-semibold mb-4">Your Plans</h3>
                <div className="space-y-3">
                  {mySubscribers.plans.map((plan) => (
                    <div key={plan.plan_id} className="p-3 bg-white/5 rounded-xl">
                      <p className="font-medium text-sm">{plan.name}</p>
                      <div className="flex items-center justify-between mt-1 text-xs text-muted-foreground">
                        <span>{formatCurrency(plan.price)}/mo</span>
                        <span>{plan.subscriber_count} subscribers</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* How It Works */}
            <div className="bg-card border border-white/10 rounded-xl p-5">
              <h3 className="font-semibold mb-4">How Subscriptions Work</h3>
              <div className="space-y-3 text-sm text-muted-foreground">
                <div className="flex items-start gap-3"><Star className="w-5 h-5 text-yellow-500 flex-shrink-0" /><p>Subscribe to your favorite creators for exclusive content and perks</p></div>
                <div className="flex items-start gap-3"><Calendar className="w-5 h-5 text-primary flex-shrink-0" /><p>Billed monthly from your LekeeWallet balance</p></div>
                <div className="flex items-start gap-3"><X className="w-5 h-5 text-muted-foreground flex-shrink-0" /><p>Cancel anytime - no commitment</p></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
};

export default SubscriptionsPage;
